import { Component, inject, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { LvrButton } from '../lvr-button/lvr-button';
import { TranslocoPipe } from '@jsverse/transloco';
export interface ActionMenuItem {
	label: string;
	icon?: string;
	tooltip?: string;
	disabled?: boolean;
	ariaLabel?: string;

	// Navigation
	routerLink?: string | string[];
	queryParams?: Record<string, string>;
	href?: string;
	target?: '_self' | '_blank';
	rel?: string;

	// Optional additional logic for each item
	onClick?: () => void;
	showLoading?: boolean;
	loadingLabel?: string;
}
@Component({
	selector: 'lvr-action-menu',
	imports: [MatTooltipModule, LvrButton, TranslocoPipe],
	templateUrl: './action-menu.html',
	styleUrl: './action-menu.scss',
})
/**
 * A reusable menu action component that emits the selected item action.
 * On click on each item does the onClick action.
 */
export class LvrActionMenu {
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	/**
	 * A list of items to be displayed in the menu.
	 */
	public readonly items = input.required<ActionMenuItem[]>();
	/**
	 * The icon to be used for the menu.
	 * Default is the addition sign: 'add'
	 */
	public readonly menuIcon = input('add');
	/**
	 * The aria label used for the icon button and the tooltip.
	 * Default is set to 'Menü öffnen'.
	 */
	public readonly buttonAriaLabel = input<string | undefined>('Menü öffnen');
	/**
	 * Whether or not the menu button should be disabled.
	 * Default is set to 'false'.
	 */
	public readonly menuDisabled = input(false);
	/**
	 * Horizontal position of the menu.
	 * Default is set to 'after'.
	 */
	public readonly xPosition = input<'before' | 'after'>('after');
	/**
	 * Vertical position of the menu.
	 * Default is set to 'below'.
	 */
	public readonly yPosition = input<'above' | 'below'>('below');
	/**
	 * Tooltip position for the action button.
	 * Default is set to 'below'.
	 */
	public readonly tooltipPosition = input<'above' | 'below' | 'left' | 'right'>('below');
	/**
	 * Emits the item action on click when occurs.
	 */
	public readonly action = output<ActionMenuItem>();
	/**
	 * Helper function for tracking items.
	 */
	public readonly trackByItem = (index: number, item: ActionMenuItem) => item.label ?? index;

	/**
	 * Handles item clicks and emits the action.
	 */
	public onItemClick(item: ActionMenuItem, event: MouseEvent): void {
		if (item.disabled) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		item.onClick?.();
		this.action.emit(item);

		if (item.routerLink) {
			this.router.navigate(Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink], {
				queryParams: item.queryParams,
				relativeTo: this.route,
			});
			return;
		}

		if (item.href) {
			if (item.target === '_blank') {
				window.open(item.href, '_blank', 'noopener,noreferrer');
			} else {
				window.location.assign(item.href);
			}
		}
	}
}
